// ISO/IEC 15189:2022 Requirements Assessment Table
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  Button,
  Card,
  CardContent,
  Fade,
  Zoom,
  Tooltip,
  Badge,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CardMedia,
} from "@mui/material";
import {
  ExpandMore,
  AssignmentTurnedIn,
  CheckCircle,
  Send,
  Lightbulb,
  FilterList,
  Category,
  Refresh,
  CloudUpload,
  Close,
  Image as ImageIcon,
  AttachFile,
  Save,
} from "@mui/icons-material";
import {
  getDAQMSSubCategories,
  getDAQMSFields,
  getDAQMSStandards,
  getDAQMSCategories,
  getDAQMSCategoryNames,
  getDAQMSQuestions,
  getNCStatements,
  getImplementationData,
  getTemplatesData,
  saveAssessmentRemark,
  getAssessmentRemarks,
  // saveEvidence,
  // getEvidence,
} from "../../services/apiCalibration";
import { useLocation } from 'react-router-dom';
import '../Admin/ListFormManagement.css';

// Memoized components for better performance
const MemoizedTableRow = memo(({ requirement, ncSelections, remarks, onNcChange, onRemarkChange, onSubmit, isSubmitted, getSubmitTooltip, getSubmitButtonText, getNcOptionsForRequirement, getImplementationForClause, getTemplatesForClause, getExistingRemarkForClause, hasExistingRemark, handleEvidenceModalOpen, hasEvidence }) => {
  const ncOptions = getNcOptionsForRequirement(requirement);
  const selectedNcItems = ncSelections.get(requirement.id) || new Set();
  const remarkValue = remarks.get(requirement.id) || "";
  const implementationText = getImplementationForClause(requirement.ClauseNo || requirement.clauseNo);
  const templates = getTemplatesForClause(requirement.ClauseNo || requirement.clauseNo);
  const submitted = isSubmitted(requirement.id);

  return (
    <TableRow
      sx={{
        backgroundColor: submitted ? "#f8fff8" : "inherit",
        "&:hover": {
          backgroundColor: submitted ? "#f0f8f0" : "#f5f5f5",
        },
        transition: "background-color 0.2s ease",
      }}
    >
      <TableCell sx={{ fontWeight: "bold", color: "#1976d2", minWidth: 80 }}>
        {requirement.ClauseNo || requirement.clauseNo || "N/A"}
      </TableCell>
      <TableCell sx={{ minWidth: 300 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
          {requirement.StandardStatement || requirement.statement || "No statement available"}
        </Typography>
        {implementationText && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">
              <strong>Implementation:</strong> {implementationText}
            </Typography>
          </Alert>
        )}
        {templates.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: "bold" }}>
              Document for Compliance:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
              {templates.map((template, idx) => (
                <Chip
                  key={idx}
                  label={template.FormatName}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 250 }}>
        {(() => {
          const clauseNo = requirement.ClauseNo || requirement.clauseNo;
          const existingRemark = getExistingRemarkForClause(clauseNo);
          const hasExisting = hasExistingRemark(clauseNo);
          const hasNoNcOptions = ncOptions.length === 0;
          
          // Don't show remarks field if no NC options are available
          if (hasNoNcOptions) {
            return null;
          }
          
          if (hasExisting && existingRemark) {
            return (
              <Alert 
                severity="info" 
                sx={{ 
                  backgroundColor: "#e3f2fd",
                  border: "1px solid #2196f3"
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                  Existing Remark:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {existingRemark.Remark}
                </Typography>
              </Alert>
            );
          } else {
            return (
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Enter compliance details..."
                value={remarkValue}
                onChange={(e) => onRemarkChange(requirement.id, e.target.value)}
                disabled={submitted}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: submitted ? "#f5f5f5" : "#fff",
                  },
                }}
              />
            );
          }
        })()}
      </TableCell>
      <TableCell sx={{ minWidth: 200 }}>
        {ncOptions.length > 0 ? (
          <FormGroup>
            {ncOptions.slice(0, 3).map((option) => {
              const isChecked = selectedNcItems.has(option);
              const clauseNo = requirement.ClauseNo || requirement.clauseNo;
              const hasEvidenceData = hasEvidence(option, clauseNo);
              
              return (
                <Box key={option} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={(e) => onNcChange(requirement.id, option, e.target.checked)}
                        disabled={submitted}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {option}
                      </Typography>
                    }
                    sx={{ margin: 0, flex: 1 }}
                  />
                  {isChecked && (
                    <Button
                      variant="contained"
                      size="small"
                      color={hasEvidenceData ? "success" : "error"}
                      onClick={() => handleEvidenceModalOpen(option, clauseNo)}
                      startIcon={hasEvidenceData ? <CheckCircle /> : <CloudUpload />}
                      disabled={submitted}
                      sx={{
                        ml: 1,
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {hasEvidenceData ? 'View Details' : 'Upload Evidence'}
                    </Button>
                  )}
                </Box>
              );
            })}
            {ncOptions.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{ncOptions.length - 3} more options
              </Typography>
            )}
          </FormGroup>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No NC options available
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 200, p: 2 }}>
        {(() => {
          const clauseNo = requirement.ClauseNo || requirement.clauseNo;
          const hasExisting = hasExistingRemark(clauseNo);
          const hasNoNcOptions = ncOptions.length === 0;
          
          // Don't show submit button if no NC options are available
          if (hasNoNcOptions) {
            return null;
          }
          
          // Don't show submit button if existing remarks are present
          if (hasExisting) {
            return null;
          }
          
          return (
            <Tooltip title={getSubmitTooltip(requirement.id)}>
              <span>
                <Button
                  variant={submitted ? "outlined" : "contained"}
                  color={
                    submitted
                      ? "success"
                      : selectedNcItems.size > 0
                      ? "error"
                      : "primary"
                  }
                  size="small"
                  onClick={() => onSubmit(requirement.id)}
                  disabled={submitted}
                  startIcon={submitted ? <CheckCircle /> : <Send />}
                  sx={{
                    minWidth: "120px",
                    fontWeight: "bold",
                    textTransform: "none",
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    ...(submitted && {
                      backgroundColor: "#e8f5e8",
                      borderColor: "#4caf50",
                      color: "#2e7d32",
                    }),
                  }}
                >
                  {submitted ? "Done" : getSubmitButtonText(requirement.id)}
                </Button>
              </span>
            </Tooltip>
          );
        })()}
      </TableCell>
    </TableRow>
  );
});

const MemoizedSubSection = memo(({ subSection, expandedAccordions, handleAccordionChange, questionsPerSection, initializeQuestionLimit, loadMoreQuestions, loadAllQuestions, ncSelections, remarks, handleNcChange, handleRemarkChange, handleSubmit, isSubmitted, getSubmitTooltip, getSubmitButtonText, getNcOptionsForRequirement, getImplementationForClause, getTemplatesForClause, getExistingRemarkForClause, hasExistingRemark, mainSectionId }) => {
  const sectionId = `${mainSectionId}_${subSection.id}`;
  const currentLimit = questionsPerSection.get(sectionId) || 3;
  
  const filteredAndSortedQuestions = useMemo(() => {
    if (!subSection.questions) return [];
    
    return subSection.questions
      .filter((question) => {
        const questionClause = question.ClauseNo || question.clauseNo || question.clause;
        const subsectionClause = subSection.clause;
        if (!questionClause || !subsectionClause) return false;
        return questionClause.toString() === subsectionClause.toString();
      });
  }, [subSection.questions, subSection.clause]);

  const totalQuestions = filteredAndSortedQuestions.length;
  const visibleQuestions = filteredAndSortedQuestions.slice(0, currentLimit);
  const hasMoreQuestions = totalQuestions > currentLimit;

  // Initialize question limit if not already done
  useEffect(() => {
    if (!questionsPerSection.has(sectionId) && totalQuestions > 0) {
      initializeQuestionLimit(sectionId, totalQuestions);
    }
  }, [sectionId, totalQuestions, questionsPerSection, initializeQuestionLimit]);

  return (
    <Card
      elevation={2}
      sx={{
        m: 2,
        borderRadius: 3,
        transition: "all 0.2s ease",
        "&:hover": {
          elevation: 4,
        },
      }}
    >
      <Accordion
        expanded={expandedAccordions.has(subSection.id)}
        onChange={handleAccordionChange(subSection.id)}
        sx={{
          boxShadow: "none",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore
              sx={{
                color: expandedAccordions.has(subSection.id) ? "#fff" : "#666",
                transition: "color 0.2s ease",
              }}
            />
          }
          sx={{
            background: expandedAccordions.has(subSection.id)
              ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
              : "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
            borderRadius: "12px 12px 0 0",
            minHeight: 64,
            transition: "all 0.2s ease",
            "&:hover": {
              background: expandedAccordions.has(subSection.id)
                ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Lightbulb
              sx={{
                color: expandedAccordions.has(subSection.id) ? "#fff" : "#ff9800",
                mr: 2,
                transition: "color 0.2s ease",
              }}
            />
            <Typography
              variant="body1"
              fontWeight="medium"
              color={expandedAccordions.has(subSection.id) ? "#fff" : "#333"}
              sx={{
                fontSize: "1.1rem",
                transition: "color 0.2s ease",
              }}
            >
              {subSection.clause} {subSection.title}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TableContainer
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <Table
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #e0e0e0",
                  borderBottom: "1px solid #e0e0e0",
                  "&:last-child": {
                    borderRight: "none",
                  },
                },
              }}
            >
              {totalQuestions > 0 && (
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      color: '#1976d2',
                      py: 1
                    }
                  }}>
                    <TableCell>Clause No.</TableCell>
                    <TableCell>Standard Statement</TableCell>
                    <TableCell>Compliance Requirement</TableCell>
                    <TableCell>Non-Compliance</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {visibleQuestions.map((question) => (
                  <MemoizedTableRow
                    key={question.id || question.QuestionId}
                    requirement={question}
                    ncSelections={ncSelections}
                    remarks={remarks}
                    onNcChange={handleNcChange}
                    onRemarkChange={handleRemarkChange}
                    onSubmit={handleSubmit}
                    isSubmitted={isSubmitted}
                    getSubmitTooltip={getSubmitTooltip}
                    getSubmitButtonText={getSubmitButtonText}
                    getNcOptionsForRequirement={getNcOptionsForRequirement}
                    getImplementationForClause={getImplementationForClause}
                    getTemplatesForClause={getTemplatesForClause}
                    getExistingRemarkForClause={getExistingRemarkForClause}
                    hasExistingRemark={hasExistingRemark}
                  />
                ))}
                
                {hasMoreQuestions && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => loadMoreQuestions(sectionId, totalQuestions)}
                          startIcon={<ExpandMore />}
                        >
                          Load 5 More Questions
                        </Button>
                        
                        {totalQuestions - currentLimit > 5 && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => loadAllQuestions(sectionId, totalQuestions)}
                            sx={{ ml: 1 }}
                          >
                            Load All ({totalQuestions - currentLimit} remaining)
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                
                {totalQuestions > 3 && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 1, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="caption" color="text.secondary">
                        Showing {Math.min(currentLimit, totalQuestions)} of {totalQuestions} questions for better performance
                        {currentLimit >= totalQuestions && totalQuestions > 3 && (
                          <span style={{ color: '#4caf50', fontWeight: 'bold' }}> • All questions loaded</span>
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
});

const SubmitDA = ({ data, onUpdate, fields = [], sectionType }) => {
  const location = useLocation();
  
  // Extract query parameters
  const searchParams = new URLSearchParams(location.search);
  const appId = searchParams.get('appid') || '67'; // Default value
  const orgId = searchParams.get('orgid') || '35'; // Default value  
  const assessmentId = searchParams.get('assessmentid') || '6'; // Default value

  // Create queryParams object for easy access
  const queryParams = { appId, orgId, assessmentId };

  // Dynamic field and standard configuration
  const [assessmentConfig, setAssessmentConfig] = useState({
    field: searchParams.get('field') || 'Testing', // Default to Testing, can be overridden via URL param
    standard: searchParams.get('standard') || '2017' // Default to 2017, can be overridden via URL param
  });

  console.log('Query Parameters:', { appId, orgId, assessmentId });
  const [formData] = useState({
    ...data,
  });
  const [submittedItems, setSubmittedItems] = useState(new Set());
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [ncSelections, setNcSelections] = useState(new Map()); // Maps requirement ID to Set of selected NC items
  const [remarks, setRemarks] = useState(new Map()); // Maps requirement ID to remarks text

  // DAQMS Sub Categories state
  const [tabValue, setTabValue] = useState(0); // 0 for Requirements, 1 for DAQMS Sub Categories, 2 for DAQMS Categories
  const [daqmsData, setDaqmsData] = useState([]);
  const [daqmsLoading, setDaqmsLoading] = useState(false);
  const [daqmsFields, setDaqmsFields] = useState([]);
  const [daqmsStandards, setDaqmsStandards] = useState([]);
  const [daqmsFilters, setDaqmsFilters] = useState({
    field: assessmentConfig.field, // Use dynamic field
    standard: "",
    catId: "",
    isActive: 1,
  });
  const [daqmsError, setDaqmsError] = useState("");

  // DAQMS Categories state
  const [daqmsCategoryData, setDaqmsCategoryData] = useState([]);
  const [daqmsCategoryLoading, setDaqmsCategoryLoading] = useState(false);
  const [daqmsCategories, setDaqmsCategories] = useState([]);
  const [daqmsSubCategories, setDaqmsSubCategories] = useState([]);
  const [daqmsQuestions, setDaqmsQuestions] = useState([]);
  const [daqmsCategoryFilters, setDaqmsCategoryFilters] = useState({
    field: assessmentConfig.field, // Use dynamic field
    standard: "",
    displayId: "",
    disId: "",
  });
  const [daqmsCategoryError, setDaqmsCategoryError] = useState("");

  // Dynamic Requirements state (for first tab)
  const [dynamicRequirements, setDynamicRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState("");
  const [isDataFetched, setIsDataFetched] = useState(false); // Flag to prevent multiple fetches
  
  // Lazy loading states
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [categorySubcategories, setCategorySubcategories] = useState(new Map()); // Cache subcategories by category
  const [subcategoryQuestions, setSubcategoryQuestions] = useState(new Map()); // Cache questions by subcategory

  // NC Statements state
  const [ncStatements, setNcStatements] = useState([]);
  const [ncStatementsLoading, setNcStatementsLoading] = useState(false);
  const [ncStatementsError, setNcStatementsError] = useState("");
  const [isNcStatementsFetched, setIsNcStatementsFetched] = useState(false);

  // Implementation Data state
  const [implementationData, setImplementationData] = useState([]);
  const [implementationLoading, setImplementationLoading] = useState(false);
  const [isImplementationFetched, setIsImplementationFetched] = useState(false);

  // Templates Data state
  const [templatesData, setTemplatesData] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [isTemplatesFetched, setIsTemplatesFetched] = useState(false);

  // Existing Remarks state
  const [existingRemarks, setExistingRemarks] = useState(new Map()); // Maps clause/itemId to existing remark data
  const [existingRemarksLoading, setExistingRemarksLoading] = useState(false);
  const [isExistingRemarksFetched, setIsExistingRemarksFetched] = useState(false);

  // Evidence Modal state
  const [evidenceModal, setEvidenceModal] = useState({
    open: false,
    ncItem: '',
    clauseNo: '',
    notes: '',
    file: null,
    imagePreview: null,
    uploadStatus: null
  });
  const [uploading, setUploading] = useState(false);
  const [evidenceData, setEvidenceData] = useState(new Map()); // Maps NC item to evidence data
  const fileInputRef = useRef(null);

  // Performance optimization states
  const [visibleRequirements, setVisibleRequirements] = useState(5); // Show only first 5 initially
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchFilter, setSearchFilter] = useState(""); // Add search filter to reduce visible items
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState(""); // Debounced search
  const [isInitialized, setIsInitialized] = useState(false); // Prevent multiple initializations
  const [questionsPerSection, setQuestionsPerSection] = useState(new Map()); // Track visible questions per section

  // Debounce search filter to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchFilter]);
  
  // Initialize questions display limit
  const initializeQuestionLimit = useCallback((sectionId, totalQuestions) => {
    setQuestionsPerSection(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(sectionId)) {
        newMap.set(sectionId, Math.min(3, totalQuestions)); // Show max 3 questions initially
      }
      return newMap;
    });
  }, []);

  const loadAllQuestions = useCallback((sectionId, totalQuestions) => {
    setQuestionsPerSection(prev => {
      const newMap = new Map(prev);
      newMap.set(sectionId, totalQuestions); // Show all questions
      console.log(`Loading ALL questions for ${sectionId}: showing all ${totalQuestions} questions`);
      return newMap;
    });
  }, []);

  const loadMoreQuestions = useCallback((sectionId, totalQuestions) => {
    setQuestionsPerSection(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(sectionId) || 3;
      // Always load exactly 5 more, but don't exceed total
      const newLimit = Math.min(current + 5, totalQuestions);
      newMap.set(sectionId, newLimit);
      console.log(`✅ Loading more questions for ${sectionId}: ${current} → ${newLimit} (total: ${totalQuestions})`);
      return newMap;
    });
  }, []);

  // Define NC options for specific requirements only (fallback - will be replaced by dynamic data)
  const ncOptions = useMemo(() => ({
  }), []);

  // Dynamic NC options based on fetched NC statements
  const getNcOptionsForRequirement = useCallback((questionOrRequirement) => {
    // Try to get clause number from question or requirement object
    let clauseNo = null;
    
    if (typeof questionOrRequirement === 'string') {
      console.log("getNcOptionsForRequirement: Input is a string, using as clause number:", questionOrRequirement);
      // If it's a string (requirement ID), use it as clause number
      clauseNo = questionOrRequirement;
    } else if (questionOrRequirement && typeof questionOrRequirement === 'object') {
      // If it's a question object, extract clause number from various possible properties
      clauseNo = questionOrRequirement.ClauseNo || 
                questionOrRequirement.clauseNo || 
                questionOrRequirement.clause ||
                questionOrRequirement.DisplayName ||
                questionOrRequirement.displayName;
    }
    
    if (!clauseNo) {
      console.warn("⚠️ getNcOptionsForRequirement: No clause number found for", questionOrRequirement);
      return [];
    }
    
    // Normalize the clause number for better matching
    const normalizedClauseNo = clauseNo.toString().trim();
    
    // First try to find NC statements matching the clause number
    const dynamicOptions = ncStatements.filter(statement => {
      const statementClause = (statement.clauseno || statement.ClauseNo || statement.clause || '').toString().trim();
      const isMatch = statementClause === normalizedClauseNo;
      
      if (isMatch) {
        console.log(`✅ Found NC statement match: ${statementClause} = ${normalizedClauseNo}`);
      }
      
      return isMatch;
    }).map(statement => {
      // Return the statement text from the Name field (as shown in your API response)
      return statement.Name || statement.name || statement.Statement || statement.statement || statement.text || statement.StatementText;
    }).filter(Boolean); // Remove any null/undefined values
    
    // If we found dynamic options, use them
    if (dynamicOptions.length > 0) {
      console.log(`✅ Found ${dynamicOptions.length} dynamic NC options for clause ${normalizedClauseNo}:`, dynamicOptions.slice(0, 2));
      return dynamicOptions;
    }
    
    // Fallback to static options if no dynamic options found
    const staticOptions = ncOptions[normalizedClauseNo] || [];
    if (staticOptions.length > 0) {
      console.log(`📋 Using ${staticOptions.length} static NC options for clause ${normalizedClauseNo}`);
    } else {
      console.log(`❌ No NC options found for clause ${normalizedClauseNo}`);
    }
    
    return staticOptions;
  }, [ncStatements, ncOptions]);

  // Dynamic requirements built from DAQMS data (lazy loaded)
  const requirements = useMemo(() => {
    console.log("Building requirements with:", {
      categoriesCount: daqmsCategories.length,
      subcategoriesCount: daqmsSubCategories.length,
      questionsCount: daqmsQuestions.length,
    });

    // If no categories loaded, return empty
    if (!daqmsCategories.length) {
      console.log("No categories loaded yet");
      return [];
    }

    // Sort categories by clause number
    const sortedCategories = daqmsCategories.sort((a, b) => {
      const clauseA = String(a.DisplayId || a.displayId || a.DisId || a.disId || '0');
      const clauseB = String(b.DisplayId || b.displayId || b.DisId || b.disId || '0');
      
      // Try numeric comparison first
      const numA = parseFloat(clauseA);
      const numB = parseFloat(clauseB);
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Fall back to string comparison
      return clauseA.localeCompare(clauseB);
    });

    // Build requirements structure
    const result = sortedCategories.map((category) => {
      const categoryId = category.CategoryId || category.categoryId || category.Id || category.id;
      const categoryName = category.CatName || category.categoryName || category.CategoryName || category.Name || category.name || "Unknown Category";
      const displayId = category.DisplayId || category.displayId || category.DisId || category.disId || categoryId;

      if (!categoryId) {
        return null;
      }

      // Get subcategories for this category (only if they've been loaded)
      const categorySubcats = daqmsSubCategories.filter(subcat => {
        const subCatId = subcat.CatId || subcat.catId;
        return subCatId === categoryId;
      });

      // Sort subcategories by clause number
      const sortedSubcategories = categorySubcats.sort((a, b) => {
        const clauseA = String(a.SdisId || a.DisplayId || a.DisId || '0');
        const clauseB = String(b.SdisId || b.DisplayId || b.DisId || '0');
        
        // Try numeric comparison first
        const numA = parseFloat(clauseA);
        const numB = parseFloat(clauseB);
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        // Fall back to string comparison
        return clauseA.localeCompare(clauseB);
      });

      return {
        id: categoryId.toString(),
        categoryId: categoryId,
        clause: displayId ? displayId.toString() : categoryId.toString(),
        title: categoryName,
        field: category.Field,
        standard: category.Standard,
        requirements: sortedSubcategories.map((subcat) => {
          const subCategoryId = subcat.SdisId;
          const subcatId = subcat.Id || subcat.id;
          const subCategoryName = subcat.SubCategory || "Unknown Subcategory";
          const subDisplayId = subcat.DisplayId || subcat.displayId || subcat.DisId || subcat.disId || subCategoryId;

          // Get questions for this subcategory (only if they've been loaded)
          const subcategoryQuestionsList = daqmsQuestions.filter(question => {
            const questionSubCatId = question.QSubCatId || question.SubCatId || question.qSubCatId || question.subCatId || question.subcategoryId || question.SubcategoryId;
            return questionSubCatId === subcatId;
          });

          // Sort questions by clause number
          const sortedQuestions = subcategoryQuestionsList.sort((a, b) => {
            const clauseA = String(a.DisplayName || a.displayName || a.ClauseNo || a.clauseNo || '0');
            const clauseB = String(b.DisplayName || b.displayName || b.ClauseNo || b.clauseNo || '0');
            
            // Try numeric comparison first
            const numA = parseFloat(clauseA);
            const numB = parseFloat(clauseB);
            
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
            
            // Fall back to string comparison
            return clauseA.localeCompare(clauseB);
          });

          return {
            id: subCategoryId ? subCategoryId.toString() : "unknown",
            subcatId: subcatId,
            clause: subDisplayId ? subDisplayId.toString() : subCategoryId ? subCategoryId.toString() : "unknown",
            title: subCategoryName,
            questions: sortedQuestions,
            requirements: []
          };
        }).filter((subcat) => subcat.id !== "unknown"),
      };
    }).filter(Boolean);

    return result;
  }, [daqmsCategories, daqmsSubCategories, daqmsQuestions]);

  // DAQMS Functions
  const fetchDAQMSData = useCallback(async () => {
    setDaqmsLoading(true);
    setDaqmsError("");
    try {
      const response = await getDAQMSSubCategories(daqmsFilters);
      if (response.data.success) {
        setDaqmsData(response.data.data);
      } else {
        setDaqmsError(response.data.message || "Failed to fetch DAQMS data");
      }
    } catch (error) {
      console.error("Error fetching DAQMS data:", error);
      setDaqmsError("Failed to fetch DAQMS Sub Categories");
    } finally {
      setDaqmsLoading(false);
    }
  }, [daqmsFilters]);

  // Fetch NC Statements for dynamic NC options
  const fetchNCStatements = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (ncStatementsLoading || isNcStatementsFetched) {
      console.log("🛑 fetchNCStatements: Skipped - already loading or fetched");
      return;
    }
    
    console.log("🚀 fetchNCStatements: Starting API call");
    
    try {
      setNcStatementsLoading(true);
      setNcStatementsError("");
      
      // Fetch NC statements with dynamic field and standard
      const response = await getNCStatements({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
      });
      
      if (response.data.success) {
        const fetchedNCStatements = response.data.data;
        console.log("✅ fetchNCStatements: Fetched", fetchedNCStatements.length, "NC statements");
        setNcStatements(fetchedNCStatements);
        setIsNcStatementsFetched(true);
      } else {
        throw new Error("Failed to fetch NC statements");
      }
    } catch (error) {
      console.error("❌ fetchNCStatements: Error:", error);
      setNcStatementsError("Failed to load NC statements: " + error.message);
    } finally {
      setNcStatementsLoading(false);
    }
  }, [ncStatementsLoading, isNcStatementsFetched, assessmentConfig.field, assessmentConfig.standard]);

  // Fetch Implementation Data from Compliance_Requirements table
  const fetchImplementationData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (implementationLoading || isImplementationFetched) {
      console.log("🛑 fetchImplementationData: Skipped - already loading or fetched");
      return;
    }
    
    console.log("🚀 fetchImplementationData: Starting API call");
    
    try {
      setImplementationLoading(true);
      
      // Fetch implementation data with dynamic field and standard
      const response = await getImplementationData({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
        isActive: 1
      });
      
      if (response.data.success) {
        const fetchedImplementationData = response.data.data;
        console.log("✅ fetchImplementationData: Fetched", fetchedImplementationData.length, "implementation records");
        setImplementationData(fetchedImplementationData);
        setIsImplementationFetched(true);
      } else {
        throw new Error("Failed to fetch implementation data");
      }
    } catch (error) {
      console.error("❌ fetchImplementationData: Error:", error);
    } finally {
      setImplementationLoading(false);
    }
  }, [implementationLoading, isImplementationFetched, assessmentConfig.field, assessmentConfig.standard]);

  // Fetch Templates Data from ListFormats table
  const fetchTemplatesData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (templatesLoading || isTemplatesFetched) {
      console.log("🛑 fetchTemplatesData: Skipped - already loading or fetched");
      return;
    }
    
    console.log("🚀 fetchTemplatesData: Starting API call");
    
    try {
      setTemplatesLoading(true);
      
      // Fetch templates data with dynamic field and standard
      const response = await getTemplatesData({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard
      });
      
      if (response.data.success) {
        const fetchedTemplatesData = response.data.data;
        console.log("✅ fetchTemplatesData: Fetched", fetchedTemplatesData.length, "template records");
        setTemplatesData(fetchedTemplatesData);
        setIsTemplatesFetched(true);
      } else {
        throw new Error("Failed to fetch templates data");
      }
    } catch (error) {
      console.error("❌ fetchTemplatesData: Error:", error);
    } finally {
      setTemplatesLoading(false);
    }
  }, [templatesLoading, isTemplatesFetched, assessmentConfig.field, assessmentConfig.standard]);

  // Fetch Existing Assessment Remarks
  const fetchExistingRemarks = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (existingRemarksLoading || isExistingRemarksFetched) {
      console.log("🛑 fetchExistingRemarks: Skipped - already loading or fetched");
      return;
    }
    
    console.log("🚀 fetchExistingRemarks: Starting API call with params:", { appId, orgId, assessmentId });
    
    try {
      setExistingRemarksLoading(true);
      
      // Fetch existing remarks for this assessment
      const response = await getAssessmentRemarks({
        appId: appId,
        orgId: orgId,
        assessmentId: assessmentId
      });
      
      if (response.data.success) {
        const fetchedRemarks = response.data.data;
        console.log("✅ fetchExistingRemarks: Fetched", fetchedRemarks.length, "existing remarks");
        
        // Convert array to Map for easy lookup by clause
        const remarksMap = new Map();
        fetchedRemarks.forEach(remark => {
          if (remark.Clause) {
            remarksMap.set(remark.Clause, remark);
          }
        });
        
        setExistingRemarks(remarksMap);
        setIsExistingRemarksFetched(true);
        
        // Also populate the remarks state with existing values
        const newRemarksState = new Map(remarks);
        fetchedRemarks.forEach(remark => {
          if (remark.Clause && remark.Remark) {
            // We'll need to map clause to itemId - for now use clause as key
            newRemarksState.set(remark.Clause, remark.Remark);
          }
        });
        setRemarks(newRemarksState);
        
      } else {
        throw new Error("Failed to fetch existing remarks");
      }
    } catch (error) {
      console.error("❌ fetchExistingRemarks: Error:", error);
    } finally {
      setExistingRemarksLoading(false);
    }
  }, [existingRemarksLoading, isExistingRemarksFetched, appId, orgId, assessmentId, remarks]);

  // Fetch existing evidence data
  // const fetchExistingEvidence = useCallback(async () => {
  //   if (!appId || !orgId || !assessmentId) {
  //     return;
  //   }
    
  //   console.log("🚀 fetchExistingEvidence: Starting API call with params:", { appId, orgId, assessmentId });
    
  //   try {
  //     const response = await getEvidence({
  //       appId: appId,
  //       orgId: orgId,
  //       assessmentId: assessmentId
  //     });
      
  //     if (response.data.success) {
  //       const fetchedEvidence = response.data.data;
  //       console.log("✅ fetchExistingEvidence: Fetched", fetchedEvidence.length, "evidence records");
        
  //       // Convert array to Map for easy lookup by NC item and clause
  //       const evidenceMap = new Map();
  //       fetchedEvidence.forEach(evidence => {
  //         if (evidence.ncItem && evidence.clauseNo) {
  //           const evidenceKey = `${evidence.ncItem}_${evidence.clauseNo}`;
  //           evidenceMap.set(evidenceKey, {
  //             ncItem: evidence.ncItem,
  //             clauseNo: evidence.clauseNo,
  //             notes: evidence.notes,
  //             imageUrl: evidence.imageUrl,
  //             uploadDate: evidence.InsertDate,
  //             evidenceId: evidence.id
  //           });
  //         }
  //       });
        
  //       setEvidenceData(evidenceMap);
        
  //     } else {
  //       console.warn("Failed to fetch existing evidence:", response.data.message);
  //     }
  //   } catch (error) {
  //     console.error("❌ fetchExistingEvidence: Error:", error);
  //   }
  // }, [appId, orgId, assessmentId]);

  // Get implementation text for a specific clause number
  const getImplementationForClause = useCallback((clauseNo) => {
    if (!clauseNo || !implementationData.length) {
      return "";
    }
    
    const normalizedClauseNo = clauseNo.toString().trim();
    const implementation = implementationData.find(item => {
      const itemClause = (item.clauseno || item.ClauseNo || '').toString().trim();
      return itemClause === normalizedClauseNo;
    });
    
    if (implementation) {
      console.log(`✅ Found implementation for clause ${normalizedClauseNo}`);
      // Use 'Name' field from your API response
      return implementation.Name || implementation.name || implementation.Implementation || implementation.implementation || "";
    }
    
    console.log(`❌ No implementation found for clause ${normalizedClauseNo}`);
    return "";
  }, [implementationData]);

  // Get templates for a specific clause number
  const getTemplatesForClause = useCallback((clauseNo) => {
    if (!clauseNo || !templatesData.length) {
      return [];
    }
    
    const normalizedClauseNo = clauseNo.toString().trim();
    const templates = templatesData.filter(item => {
      const itemClause = (item.clauseno || item.ClauseNo || '').toString().trim();
      return itemClause === normalizedClauseNo;
    });
    
    if (templates.length > 0) {
      console.log(`✅ Found ${templates.length} template(s) for clause ${normalizedClauseNo}`);
      return templates;
    }
    
    console.log(`❌ No templates found for clause ${normalizedClauseNo}`);
    return [];
  }, [templatesData]);

  // Get existing remark for a specific clause or item
  const getExistingRemarkForClause = useCallback((clauseNo) => {
    if (!clauseNo || !existingRemarks.size) {
      return null;
    }
    
    const normalizedClauseNo = clauseNo.toString().trim();
    const existingRemark = existingRemarks.get(normalizedClauseNo);
    
    if (existingRemark) {
      console.log(`✅ Found existing remark for clause ${normalizedClauseNo}:`, existingRemark.Remark);
      return existingRemark;
    }
    
    return null;
  }, [existingRemarks]);

  // Check if a clause has existing remark
  const hasExistingRemark = useCallback((clauseNo) => {
    return getExistingRemarkForClause(clauseNo) !== null;
  }, [getExistingRemarkForClause]);

  // Evidence Modal Functions
  const handleEvidenceModalOpen = useCallback((ncItem, clauseNo) => {
    // Load existing evidence if any
    const existingEvidence = evidenceData.get(`${ncItem}_${clauseNo}`);
    
    setEvidenceModal({
      open: true,
      ncItem: ncItem,
      clauseNo: clauseNo,
      notes: existingEvidence?.notes || '',
      file: null,
      imagePreview: existingEvidence?.imageUrl || null,
      uploadStatus: null
    });
  }, [evidenceData]);

  const handleEvidenceModalClose = useCallback(() => {
    setEvidenceModal({
      open: false,
      ncItem: '',
      clauseNo: '',
      notes: '',
      file: null,
      imagePreview: null,
      uploadStatus: null
    });
  }, []);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (images only)
    if (!file.type.startsWith('image/')) {
      setEvidenceModal(prev => ({
        ...prev,
        uploadStatus: 'Error: Please select an image file'
      }));
      return;
    }

    // Validate file size (e.g., max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setEvidenceModal(prev => ({
        ...prev,
        uploadStatus: 'Error: File size must be less than 5MB'
      }));
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    setEvidenceModal(prev => ({
      ...prev,
      file: file,
      imagePreview: previewUrl,
      uploadStatus: null
    }));
  }, []);

  // const handleSaveEvidence = useCallback(async () => {
  //   if (!evidenceModal.ncItem || !evidenceModal.clauseNo) {
  //     setEvidenceModal(prev => ({
  //       ...prev,
  //       uploadStatus: 'Error: Missing required information'
  //     }));
  //     return;
  //   }

  //   if (!evidenceModal.file && !evidenceModal.notes.trim()) {
  //     setEvidenceModal(prev => ({
  //       ...prev,
  //       uploadStatus: 'Error: Please provide either an image or notes'
  //     }));
  //     return;
  //   }

  //   setUploading(true);
  //   setEvidenceModal(prev => ({
  //     ...prev,
  //     uploadStatus: null
  //   }));

  //   try {
  //     let imageUrl = null;

  //     // If there's a file, simulate S3 upload
  //     if (evidenceModal.file) {
  //       // TODO: Replace with actual S3 upload
  //       // For now, simulate upload delay and create a mock URL
  //       await new Promise(resolve => setTimeout(resolve, 1000));
  //       imageUrl = `https://your-bucket.s3.amazonaws.com/evidence/${Date.now()}_${evidenceModal.file.name}`;
  //     }

  //     // Save evidence to database via API
  //     const evidenceData = {
  //       ncItem: evidenceModal.ncItem,
  //       clauseNo: evidenceModal.clauseNo,
  //       notes: evidenceModal.notes.trim() || null,
  //       imageUrl: imageUrl,
  //       appId: queryParams.appId,
  //       orgId: queryParams.orgId,
  //       assessmentId: queryParams.assessmentId
  //     };

  //     const response = await saveEvidence(evidenceData);

  //     if (response.data.success) {
  //       // Update local evidence data
  //       const evidenceKey = `${evidenceModal.ncItem}_${evidenceModal.clauseNo}`;
  //       setEvidenceData(prev => {
  //         const newMap = new Map(prev);
  //         newMap.set(evidenceKey, {
  //           ...evidenceData,
  //           uploadDate: new Date(),
  //           evidenceId: response.data.evidenceId
  //         });
  //         return newMap;
  //       });

  //       setEvidenceModal(prev => ({
  //         ...prev,
  //         uploadStatus: 'Evidence saved successfully!'
  //       }));

  //       // Close modal after a short delay
  //       setTimeout(() => {
  //         handleEvidenceModalClose();
  //       }, 1500);

  //     } else {
  //       throw new Error(response.data.message || 'Failed to save evidence');
  //     }
      
  //   } catch (error) {
  //     console.error('Error saving evidence:', error);
  //     setEvidenceModal(prev => ({
  //       ...prev,
  //       uploadStatus: 'Error: ' + (error.response?.data?.message || error.message || 'Failed to save evidence')
  //     }));
  //   } finally {
  //     setUploading(false);
  //   }
  // }, [evidenceModal, queryParams, handleEvidenceModalClose]);

  const hasEvidence = useCallback((ncItem, clauseNo) => {
    return evidenceData.has(`${ncItem}_${clauseNo}`);
  }, [evidenceData]);

  // Fetch DAQMS Categories and SubCategories for dynamic requirements
  const fetchDAQMSRequirementsData = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (requirementsLoading || isDataFetched) {
      console.log("🛑 fetchDAQMSRequirementsData: Skipped - already loading or fetched");
      return;
    }
    
    console.log("🚀 fetchDAQMSRequirementsData: Starting API calls - ONLY CATEGORIES");
    
    try {
      setRequirementsLoading(true);
      
      // Fetch ONLY categories on initial load
      const categoriesResponse = await getDAQMSCategories({ field: assessmentConfig.field });
      if (categoriesResponse.data.success) {
        setDaqmsCategories(categoriesResponse.data.data);
        console.log("✅ Loaded", categoriesResponse.data.data.length, "categories");
      }
      
      setIsDataFetched(true);
      console.log("✅ fetchDAQMSRequirementsData: Completed successfully (categories only)");
    } catch (error) {
      console.error("❌ fetchDAQMSRequirementsData: Error:", error);
    } finally {
      setRequirementsLoading(false);
    }
  }, [requirementsLoading, isDataFetched, assessmentConfig.field]);

  // Fetch subcategories for a specific category
  const fetchSubcategoriesForCategory = useCallback(async (categoryId, categoryName) => {
    // Check cache first
    if (categorySubcategories.has(categoryId)) {
      console.log("✅ Using cached subcategories for category:", categoryName);
      return categorySubcategories.get(categoryId);
    }
    
    console.log("🚀 Fetching subcategories for category:", categoryName, "ID:", categoryId);
    setLoadingSubcategories(true);
    
    try {
      const response = await getDAQMSSubCategories({
        field: assessmentConfig.field,
        catId: categoryId,
        isActive: 1,
      });
      
      if (response.data.success) {
        const subcategories = response.data.data;
        console.log("✅ Loaded", subcategories.length, "subcategories for", categoryName);
        
        // Cache the subcategories
        setCategorySubcategories(prev => {
          const newMap = new Map(prev);
          newMap.set(categoryId, subcategories);
          return newMap;
        });
        
        // Update global state
        setDaqmsSubCategories(prevSubs => {
          // Merge with existing subcategories
          const existingIds = new Set(prevSubs.map(s => s.Id || s.id));
          const newSubs = subcategories.filter(s => !existingIds.has(s.Id || s.id));
          return [...prevSubs, ...newSubs];
        });
        
        return subcategories;
      }
    } catch (error) {
      console.error("❌ Error fetching subcategories:", error);
      return [];
    } finally {
      setLoadingSubcategories(false);
    }
  }, [categorySubcategories, assessmentConfig.field]);

  // Fetch questions and related data for a specific subcategory
  const fetchQuestionsForSubcategory = useCallback(async (subcategoryId, subcategoryName, clauseNo) => {
    // Check cache first
    if (subcategoryQuestions.has(subcategoryId)) {
      console.log("✅ Using cached questions for subcategory:", subcategoryName);
      return subcategoryQuestions.get(subcategoryId);
    }
    
    console.log("🚀 Fetching questions for subcategory:", subcategoryName, "ID:", subcategoryId);
    setLoadingQuestions(true);
    
    try {
      // Fetch questions for this subcategory - API will filter by subCatId
      const questionsResponse = await getDAQMSQuestions({
        field: assessmentConfig.field,
        standard: assessmentConfig.standard,
        subCategoryId: subcategoryId // Pass subcategory ID to API
      });
      
      if (questionsResponse.data.success) {
        const questions = questionsResponse.data.data;
        console.log("✅ Loaded", questions.length, "questions for", subcategoryName);
        
        // Cache the questions
        setSubcategoryQuestions(prev => {
          const newMap = new Map(prev);
          newMap.set(subcategoryId, questions);
          return newMap;
        });
        
        // Update global state
        setDaqmsQuestions(prevQuestions => {
          // Merge with existing questions
          const existingIds = new Set(prevQuestions.map(q => q.id || q.Id));
          const newQuestions = questions.filter(q => !existingIds.has(q.id || q.Id));
          return [...prevQuestions, ...newQuestions];
        });
        
        // Fetch related data for this subcategory's clause number
        if (clauseNo) {
          // Fetch NC Statements if not already fetched
          if (!isNcStatementsFetched) {
            await fetchNCStatements();
          }
          
          // Fetch Implementation Data if not already fetched
          if (!isImplementationFetched) {
            await fetchImplementationData();
          }
          
          // Fetch Templates Data if not already fetched
          if (!isTemplatesFetched) {
            await fetchTemplatesData();
          }
          
          // Fetch Existing Remarks if not already fetched
          if (!isExistingRemarksFetched) {
            await fetchExistingRemarks();
          }
        }
        
        return questions;
      }
    } catch (error) {
      console.error("❌ Error fetching questions:", error);
      return [];
    } finally {
      setLoadingQuestions(false);
    }
  }, [subcategoryQuestions, assessmentConfig.field, assessmentConfig.standard, isNcStatementsFetched, isImplementationFetched, isTemplatesFetched, isExistingRemarksFetched, fetchNCStatements, fetchImplementationData, fetchTemplatesData, fetchExistingRemarks]);

  // Handle category click/expansion
  const handleCategoryExpand = useCallback(async (category) => {
    // Use categoryId (numeric ID)
    const categoryId = category.categoryId || category.Id || category.id;
    const categoryName = category.title || category.CatName || category.categoryName || category.Name;
    
    console.log("📂 Category clicked:", categoryName, "ID:", categoryId);
    
    // Validate that categoryId is numeric
    if (!categoryId || isNaN(categoryId)) {
      console.error("❌ Invalid category ID:", categoryId);
      return;
    }
    
    setSelectedCategory(categoryId);
    
    // Fetch subcategories for this category
    await fetchSubcategoriesForCategory(categoryId, categoryName);
  }, [fetchSubcategoriesForCategory]);

  // Handle subcategory click/expansion
  const handleSubcategoryExpand = useCallback(async (subcategory) => {
    // Use subcatId (numeric ID) instead of id (clause number string)
    const subcategoryId = subcategory.subcatId || subcategory.Id || subcategory.id;
    const subcategoryName = subcategory.title || subcategory.SubCategory || subcategory.subCategory;
    const clauseNo = subcategory.clause || subcategory.SdisId || subcategory.sdisId;
    
    console.log("📄 Subcategory clicked:", subcategoryName, "ID:", subcategoryId);
    
    // Validate that subcategoryId is numeric
    if (!subcategoryId || isNaN(subcategoryId)) {
      console.error("❌ Invalid subcategory ID:", subcategoryId);
      return;
    }
    
    setSelectedSubCategory(subcategoryId);
    
    // Fetch questions and related data for this subcategory
    await fetchQuestionsForSubcategory(subcategoryId, subcategoryName, clauseNo);
  }, [fetchQuestionsForSubcategory]);

  const fetchDAQMSFields = useCallback(async () => {
    try {
      const response = await getDAQMSFields();
      if (response.data.success) {
        setDaqmsFields(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching DAQMS fields:", error);
    }
  }, []);

  const fetchDAQMSStandards = useCallback(async () => {
    try {
      const response = await getDAQMSStandards();
      if (response.data.success) {
        setDaqmsStandards(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching DAQMS standards:", error);
    }
  }, []);

  const handleDAQMSFilterChange = useCallback((filterName, value) => {
    setDaqmsFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  // DAQMS Categories Functions
  const fetchDAQMSCategoryData = useCallback(async () => {
    setDaqmsCategoryLoading(true);
    setDaqmsCategoryError("");
    try {
      const response = await getDAQMSCategories(daqmsCategoryFilters);
      if (response.data.success) {
        setDaqmsCategoryData(response.data.data);
      } else {
        setDaqmsCategoryError(
          response.data.message || "Failed to fetch DAQMS Categories data"
        );
      }
    } catch (error) {
      console.error("Error fetching DAQMS Categories data:", error);
      setDaqmsCategoryError("Failed to fetch DAQMS Categories");
    } finally {
      setDaqmsCategoryLoading(false);
    }
  }, [daqmsCategoryFilters]);

  const fetchDAQMSCategoryNames = useCallback(async () => {
    try {
      const response = await getDAQMSCategoryNames();
      if (response.data.success) {
        // Category names fetched successfully
      }
    } catch (error) {
      console.error("Error fetching DAQMS category names:", error);
    }
  }, []);

  const handleDAQMSCategoryFilterChange = useCallback((filterName, value) => {
    setDaqmsCategoryFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  // Build dynamic requirements from DAQMS data
  const buildDynamicRequirements = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (requirementsLoading) {
      console.log("🛑 buildDynamicRequirements: Skipped - already loading");
      return;
    }
    
    console.log("🚀 buildDynamicRequirements: Starting build process");
    
    setRequirementsLoading(true);
    setRequirementsError("");

    try {
      // Fetch categories first with dynamic field filter
      const categoriesResponse = await getDAQMSCategories({ field: assessmentConfig.field });
      if (!categoriesResponse.data.success) {
        throw new Error("Failed to fetch categories");
      }

      const categories = categoriesResponse.data.data;
      
      // Fetch ALL subcategories at once instead of making individual calls
      const allSubCategoriesResponse = await getDAQMSSubCategories({
        field: assessmentConfig.field, // Filter by dynamic field instead of individual catId
        isActive: 1,
      });

      if (!allSubCategoriesResponse.data.success) {
        throw new Error("Failed to fetch subcategories");
      }

      const allSubCategories = allSubCategoriesResponse.data.data;
      const dynamicReqs = [];

      // Group subcategories by category ID for efficient processing
      const subCategoriesByCategory = allSubCategories.reduce((acc, subCat) => {
        const catId = subCat.CatId || subCat.catId;
        if (!acc[catId]) {
          acc[catId] = [];
        }
        acc[catId].push(subCat);
        return acc;
      }, {});

      console.log(`Processing ${categories.length} categories with grouped subcategories (total: ${allSubCategories.length})`);

      // Sort categories by clause number
      const sortedCategories = categories.sort((a, b) => {
        const clauseA = String(a.DisplayId || a.DisId || '0');
        const clauseB = String(b.DisplayId || b.DisId || '0');
        
        // Try numeric comparison first
        const numA = parseFloat(clauseA);
        const numB = parseFloat(clauseB);
        
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        // Fall back to string comparison
        return clauseA.localeCompare(clauseB);
      });

      // Process each category with its pre-grouped subcategories
      for (const category of sortedCategories) {
        const subCategories = subCategoriesByCategory[category.Id] || [];

        if (subCategories.length > 0) {
          // Sort subcategories by clause number
          const sortedSubCategories = subCategories.sort((a, b) => {
            const clauseA = String(a.SDisplayId || a.DisplayId || a.DisId || a.SdisId || '0');
            const clauseB = String(b.SDisplayId || b.DisplayId || b.DisId || b.SdisId || '0');
            
            // Try numeric comparison first
            const numA = parseFloat(clauseA);
            const numB = parseFloat(clauseB);
            
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
            
            // Fall back to string comparison
            return clauseA.localeCompare(clauseB);
          });

          // Build category structure
          const categoryStructure = {
            id: category.Id.toString(),
            clause: category.DisplayId || category.DisId.toString(),
            title: category.CatName,
            field: category.Field,
            standard: category.Standard,
            requirements: sortedSubCategories.map((subCat) => ({
              id: subCat.Id.toString(),
              clause: subCat.SDisplayId || subCat.SdisId.toString(),
              title: subCat.SubCategory,
              category: subCat.Category,
              requirements: [
                {
                  id: `${subCat.Id}_req`,
                  clause: subCat.SDisplayId || subCat.SdisId.toString(),
                  subclause: "",
                  requirement: subCat.SubCategory,
                  implementation: "",
                  templates: "",
                  compliance: "",
                  remark: "",
                },
              ],
            })),
          };

          dynamicReqs.push(categoryStructure);
        }
      }

      setDynamicRequirements(dynamicReqs);
    } catch (error) {
      console.error("Error building dynamic requirements:", error);
      setRequirementsError("Failed to load requirements data");
    } finally {
      setRequirementsLoading(false);
    }
  }, [requirementsLoading, assessmentConfig.field]);

  const handleTabChange = useCallback(
    (event, newValue) => {
      // Prevent tab changes while loading
      if (requirementsLoading || daqmsLoading || daqmsCategoryLoading) {
        console.log("Tab change prevented: data is still loading");
        return;
      }
      
      setTabValue(newValue);
      if (newValue === 0 && requirements.length === 0) {
        // For first tab, fetch DAQMS requirements data
        fetchDAQMSRequirementsData();
      } else if (newValue === 0 && dynamicRequirements.length === 0) {
        buildDynamicRequirements();
      } else if (newValue === 1 && daqmsData.length === 0) {
        fetchDAQMSData();
      } else if (newValue === 2 && daqmsCategoryData.length === 0) {
        fetchDAQMSCategoryData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      daqmsData.length,
      daqmsCategoryData.length,
      dynamicRequirements.length,
      requirements.length,
      requirementsLoading,
      daqmsLoading,
      daqmsCategoryLoading,
      // Functions excluded to prevent infinite loops
      // fetchDAQMSData,
      // fetchDAQMSCategoryData,
      // buildDynamicRequirements,
      // fetchDAQMSRequirementsData,
    ]
  );

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  useEffect(() => {
    // Fetch DAQMS data for dynamic requirements on component mount
    if (!isDataFetched && !requirementsLoading) {
      // Use flag to prevent re-execution
      fetchDAQMSRequirementsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched, requirementsLoading]); // Remove function dependency to prevent loops

  useEffect(() => {
    // Calculate progress based on submitted items
    const currentReqs = tabValue === 0 ? requirements : dynamicRequirements;
    const totalItems = currentReqs.reduce(
      (acc, section) =>
        acc +
        section.requirements.reduce(
          (subAcc, subSection) => subAcc + subSection.requirements.length,
          0
        ),
      0
    );
    const completedItems = submittedItems.size;
    // Progress calculation (not currently displayed but available for future use)
    const currentProgress =
      totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
    console.log("Assessment progress:", currentProgress + "%");
  }, [submittedItems, dynamicRequirements, requirements, tabValue]);

  // Initialize DAQMS data on component mount
  useEffect(() => {
    // Only run once on mount, ignore function dependencies to prevent loops
    if (!isInitialized) {
      setIsInitialized(true);
      fetchDAQMSFields();
      fetchDAQMSStandards();
      fetchDAQMSCategoryNames();
      fetchNCStatements(); // Fetch NC statements for dynamic NC options
      fetchImplementationData(); // Fetch implementation data
      fetchTemplatesData(); // Fetch templates data
      fetchExistingRemarks(); // Fetch existing assessment remarks
      // fetchExistingEvidence(); // Fetch existing evidence data
      // Load dynamic requirements for first tab by default
      buildDynamicRequirements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]); // Only depend on initialization flag

  // Fetch DAQMS data when filters change
  useEffect(() => {
    if (tabValue === 1) {
      // Use a flag or timeout to prevent rapid calls
      const timeoutId = setTimeout(() => {
        fetchDAQMSData();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daqmsFilters, tabValue]); // Remove function dependency

  // Fetch DAQMS Category data when filters change
  useEffect(() => {
    if (tabValue === 2) {
      // Use a flag or timeout to prevent rapid calls
      const timeoutId = setTimeout(() => {
        fetchDAQMSCategoryData();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daqmsCategoryFilters, tabValue]); // Remove function dependency

  const handleSubmit = useCallback(async (itemId) => {
    try {
      // Get the remark for this item
      const remarkText = remarks.get(itemId) || '';
      
      // Get the clause number from the item ID 
      // This is a simplified approach - you might need to adjust based on your data structure
      let clauseNo = null;
      let sectionId = null;
      
      // Extract clause number from requirements structure
      for (const section of requirements) {
        for (const subSection of section.requirements) {
          // Check if it's a question in the subsection
          if (subSection.questions) {
            for (let qIndex = 0; qIndex < subSection.questions.length; qIndex++) {
              const questionItemId = `${subSection.id}_q_${qIndex}`;
              if (questionItemId === itemId) {
                const question = subSection.questions[qIndex];
                clauseNo = question.DisplayName || question.ClauseNo || question.clauseNo;
                sectionId = subSection.id;
                break;
              }
            }
          }
          
          // Check if it's a requirement in the subsection
          for (const requirement of subSection.requirements) {
            if (requirement.id === itemId) {
              clauseNo = requirement.clause;
              sectionId = subSection.id;
              break;
            }
          }
        }
      }

      console.log('Submitting remark:', {
        itemId,
        clauseNo,
        sectionId,
        remarkText,
        appId,
        orgId,
        assessmentId
      });

      // Save remark to database if there's a remark to save
      if (remarkText && remarkText.trim() !== '') {
        const remarkData = {
          appId: parseInt(appId),
          orgId: parseInt(orgId),
          assessmentId: parseInt(assessmentId),
          sectionId: sectionId,
          observation: null, // You can add observation logic if needed
          clause: clauseNo,
          remark: remarkText.trim()
        };

        const response = await saveAssessmentRemark(remarkData);
        
        if (response.data.success) {
          console.log('✅ Remark saved successfully:', response.data.message);
        } else {
          console.error('❌ Failed to save remark:', response.data.message);
          // Don't prevent submission if remark saving fails
        }
      }

      // Mark item as submitted
      setSubmittedItems((prev) => new Set([...prev, itemId]));
      console.log(`✅ Submit completed for ${itemId}`);
      
    } catch (error) {
      console.error('❌ Error during submit:', error);
      // Still mark as submitted even if API call fails to prevent blocking user
      setSubmittedItems((prev) => new Set([...prev, itemId]));
    }
  }, [remarks, requirements, appId, orgId, assessmentId]);

  const handleRemarkChange = useCallback((itemId, value) => {
    setRemarks((prev) => {
      const newMap = new Map(prev);
      newMap.set(itemId, value);
      return newMap;
    });
    console.log(`Remark updated for ${itemId}:`, value);
  }, []);

  const handleNcChange = useCallback((itemId, ncOption, checked) => {
    console.log(`🔄 handleNcChange called:`, { itemId, ncOption, checked });
    
    setNcSelections((prev) => {
      const newMap = new Map(prev);
      const currentSelections = newMap.get(itemId) || new Set();
      
      console.log(`📋 Current selections for ${itemId}:`, Array.from(currentSelections));

      if (checked) {
        currentSelections.add(ncOption);
        console.log(`✅ Added NC option: ${ncOption}`);
      } else {
        currentSelections.delete(ncOption);
        console.log(`❌ Removed NC option: ${ncOption}`);
      }

      newMap.set(itemId, currentSelections);
      console.log(`💾 Updated selections for ${itemId}:`, Array.from(currentSelections));
      console.log(`📊 Total ncSelections size:`, newMap.size);
      
      return newMap;
    });
    console.log(`NC checkbox updated for ${itemId}:`, ncOption, checked);
  }, []);

  const getSubmitTooltip = (itemId) => {
    if (isSubmitted(itemId)) {
      return "Already submitted";
    }
    const selectedNcItems = ncSelections.get(itemId) || new Set();
    const hasRemarks = remarks.get(itemId)?.trim() || "";

    if (selectedNcItems.size > 0 && hasRemarks) {
      return "Submit with non-conformance and remarks";
    } else if (selectedNcItems.size > 0) {
      return "Submit with non-conformance";
    } else if (hasRemarks) {
      return "Submit with remarks";
    } else {
      return "Submit as compliant";
    }
  };

  const getSubmitButtonText = (itemId) => {
    const selectedNcItems = ncSelections.get(itemId) || new Set();
    const hasNcSelected = selectedNcItems.size > 0;
    return hasNcSelected ? "Submit as Non-Compliance" : "Submit as Compliance";
  };

  const handleAccordionChange = useCallback((accordionId, type = 'subsection', data = null) => (event, isExpanded) => {
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(accordionId);
        
        // Trigger lazy loading based on type
        if (type === 'category' && data) {
          // Load subcategories for this category
          handleCategoryExpand(data);
        } else if (type === 'subcategory' && data) {
          // Load questions for this subcategory
          handleSubcategoryExpand(data);
        }
      } else {
        newSet.delete(accordionId);
      }
      return newSet;
    });
  }, [handleCategoryExpand, handleSubcategoryExpand]);

  const isSubmitted = (itemId) => submittedItems.has(itemId);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Loading State for Dynamic Requirements */}
      {requirementsLoading && (
        <>
          {/* Loading Skeleton for Better UX */}
          <Box sx={{ mb: 4 }}>
                {[1, 2, 3].map((item) => (
                  <Card key={item} elevation={2} sx={{ mb: 2, p: 2 }}>
                    <Skeleton variant="text" width="30%" height={32} />
                    <Skeleton variant="rectangular" width="100%" height={120} sx={{ mt: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Skeleton variant="rectangular" width={100} height={36} />
                      <Skeleton variant="rectangular" width={100} height={36} />
                      <Skeleton variant="rectangular" width={80} height={36} />
                    </Box>
                  </Card>
                ))}
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading requirements...</Typography>
              </Box>
            </>
          )}

          {/* Error State for Dynamic Requirements */}
          {requirementsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {requirementsError}
              <Button
                onClick={buildDynamicRequirements}
                sx={{ ml: 2 }}
                size="small"
                variant="outlined"
              >
                Retry
              </Button>
            </Alert>
          )}

          {/* Show loading message for requirements */}
          {(!daqmsCategories.length || !daqmsSubCategories.length) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading medical requirements data...
            </Alert>
          )}

          {/* Show error if no requirements available */}
          {daqmsCategories.length === 0 && daqmsSubCategories.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No medical requirements data available. Please check your database
              connection.
            </Alert>
          )}

          {/* Search and Pagination Controls */}
          {!requirementsLoading && requirements && requirements.length > 0 && (
            <Card elevation={2} sx={{ mb: 3, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search requirements..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    InputProps={{
                      startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      Showing {Math.min(visibleRequirements, requirements.length)} of {requirements.length} sections
                    </Typography>
                    {visibleRequirements < requirements.length && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setLoadingMore(true);
                          setTimeout(() => {
                            setVisibleRequirements(prev => prev + 5);
                            setLoadingMore(false);
                          }, 300);
                        }}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                      >
                        {loadingMore ? 'Loading...' : 'Load More'}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Card>
          )}

          {/* Enhanced Accordions with Dynamic Data */}
          {!requirementsLoading &&
            !requirementsError &&
            requirements &&
            requirements.length > 0 &&
            requirements
              .filter((section) => {
                if (!debouncedSearchFilter) return true;
                return section.title?.toLowerCase().includes(debouncedSearchFilter.toLowerCase()) ||
                       section.clause?.includes(debouncedSearchFilter) ||
                       section.requirements?.some(req => 
                         req.title?.toLowerCase().includes(debouncedSearchFilter.toLowerCase())
                       );
              })
              .slice(0, visibleRequirements)
              .map((mainSection, index) => (
              <Card
                key={mainSection.id}
                elevation={4}
                sx={{
                  mb: 3,
                  borderRadius: 4,
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    elevation: 6,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                  <Accordion
                    expanded={expandedAccordions.has(mainSection.id)}
                    onChange={handleAccordionChange(mainSection.id, 'category', mainSection)}
                    sx={{
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      background:
                        "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMore
                          sx={{ color: "#1976d2", fontSize: "2rem" }}
                        />
                      }
                      sx={{
                        background:
                          "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)",
                        minHeight: 80,
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Badge
                          badgeContent={mainSection.requirements.length}
                          color="primary"
                          sx={{ mr: 2 }}
                        >
                          <AssignmentTurnedIn
                            sx={{ color: "#1976d2", mr: 2 }}
                          />
                        </Badge>
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="#1976d2"
                          >
                            {mainSection.clause} {mainSection.title}
                          </Typography>
                          <Typography variant="caption" color="#666">
                            Field: {mainSection.field} | Standard:{" "}
                            {mainSection.standard}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Show loading state while fetching subcategories */}
                      {loadingSubcategories && expandedAccordions.has(mainSection.id) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ ml: 2 }}>Loading subcategories...</Typography>
                        </Box>
                      )}
                      
                      {/* Show subcategories if loaded */}
                      {!loadingSubcategories && mainSection.requirements.map((subSection, subIndex) => (
                        <Card
                          key={subSection.id}
                          elevation={2}
                          sx={{
                            m: 2,
                            borderRadius: 3,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              elevation: 4,
                            },
                          }}
                        >
                          <Accordion
                            expanded={expandedAccordions.has(subSection.id)}
                            onChange={handleAccordionChange(subSection.id, 'subcategory', subSection)}
                            sx={{
                              boxShadow: "none",
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMore
                                  sx={{
                                    color: expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#666",
                                    transition: "color 0.3s ease",
                                  }}
                                />
                              }
                              sx={{
                                background: expandedAccordions.has(
                                  subSection.id
                                )
                                  ? "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
                                  : "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
                                borderRadius: "12px 12px 0 0",
                                minHeight: 64,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: expandedAccordions.has(
                                    subSection.id
                                  )
                                    ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                                    : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                                },
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Lightbulb
                                  sx={{
                                    color: expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#ff9800",
                                    mr: 2,
                                    transition: "color 0.3s ease",
                                  }}
                                />
                                <Typography
                                  variant="body1"
                                  fontWeight="medium"
                                  color={
                                    expandedAccordions.has(subSection.id)
                                      ? "#fff"
                                      : "#333"
                                  }
                                  sx={{
                                    fontSize: "1.1rem",
                                    transition: "color 0.3s ease",
                                  }}
                                >
                                  {subSection.clause} {subSection.title}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                              {/* Show loading state while fetching questions */}
                              {loadingQuestions && expandedAccordions.has(subSection.id) && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                  <CircularProgress size={24} />
                                  <Typography sx={{ ml: 2 }}>Loading questions...</Typography>
                                </Box>
                              )}
                              
                              {/* Show questions table if loaded */}
                              {!loadingQuestions && (
                              <TableContainer
                                sx={{
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 2,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                  overflow: "hidden",
                                }}
                              >
                                <Table
                                  sx={{
                                    borderCollapse: "separate",
                                    borderSpacing: 0,
                                    "& .MuiTableCell-root": {
                                      borderRight: "1px solid #e0e0e0",
                                      borderBottom: "1px solid #e0e0e0",
                                      "&:last-child": {
                                        borderRight: "none",
                                      },
                                    },
                                  }}
                                >
                                  {/* Table Header for Questions */}
                                  {subSection.questions && 
                                   subSection.questions.filter((question) => {
                                     const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                     const subsectionClause = subSection.clause;
                                     if (!questionClause || !subsectionClause) return true;
                                     return questionClause.toString() === subsectionClause.toString();
                                   }).length > 0 && (
                                    <TableHead>
                                      <TableRow sx={{ 
                                        backgroundColor: '#f5f5f5',
                                        '& .MuiTableCell-head': {
                                          fontWeight: 'bold',
                                          fontSize: '0.8rem',
                                          color: '#1976d2',
                                          py: 1
                                        }
                                      }}>
                                        <TableCell>Clause No.</TableCell>
                                        <TableCell>Standard Statement</TableCell>
                                        <TableCell>Compliance Requirement</TableCell>
                                        <TableCell>Non-Compliance</TableCell>
                                        <TableCell>Action</TableCell>
                                      </TableRow>
                                    </TableHead>
                                  )}
                                  <TableBody>
                                    {/* Generate rows for each question in the subsection */}
                                    {subSection.questions && (() => {
                                      const sectionId = `${mainSection.id}_${subSection.id}`;
                                      const filteredAndSortedQuestions = subSection.questions
                                        .filter((question) => {
                                          // Filter questions to match current subsection
                                          const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                          const subsectionClause = subSection.clause;
                                          if (!questionClause || !subsectionClause) return true;
                                          return questionClause.toString() === subsectionClause.toString();
                                        });

                                      // Initialize question limit for this section
                                      const currentLimit = questionsPerSection.get(sectionId) || 3;
                                      const totalQuestions = filteredAndSortedQuestions.length;
                                      
                                      // Initialize if not already done
                                      if (!questionsPerSection.has(sectionId) && totalQuestions > 0) {
                                        initializeQuestionLimit(sectionId, totalQuestions);
                                      }

                                      const visibleQuestions = filteredAndSortedQuestions.slice(0, currentLimit);
                                      const hasMoreQuestions = totalQuestions > currentLimit;

                                      return (
                                        <>
                                          {visibleQuestions.map((question, qIndex) => (
                                        <TableRow
                                          key={`${subSection.id}_q_${qIndex}`}
                                          sx={{
                                            "&:nth-of-type(odd)": {
                                              backgroundColor: "#f8f9fa",
                                            },
                                            "&:nth-of-type(even)": {
                                              backgroundColor: "#ffffff",
                                            },
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(33, 150, 243, 0.08)",
                                            },
                                            "&:last-child .MuiTableCell-root": {
                                              borderBottom: "none",
                                            },
                                            transition: "all 0.2s ease",
                                          }}
                                        >
                                          <TableCell
                                            sx={{
                                              minWidth: 100,
                                              fontWeight: "bold",
                                              color: "#000",
                                              fontSize: "1.1rem",
                                              p: 2,
                                            }}
                                          >
                                            {question.DisplayName}
                                          </TableCell>
                                          <TableCell
                                           dangerouslySetInnerHTML={{ 
                        __html: question.Question || 'N/A' 
                      }}
                                            sx={{ minWidth: 200, p: 2, verticalAlign: "top" }}
                                          >
                                            {/* <Box sx={{ mb: 1 }}>
                                            
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize: "0.9rem",
                                                  lineHeight: 1.6,
                                                  color: "#000",
                                                  p: 1.5,
                                                }}
                                              >
                                                {question.Question}
                                              </Typography>
                                            </Box> */}
                                          </TableCell>
                                          <TableCell  sx={{ p: 2, verticalAlign: "top"  }}>
                                            {(() => {
                                              const questionClause = question.DisplayName || question.ClauseNo || question.clauseNo;
                                              const implementationText = getImplementationForClause(questionClause);
                                              const availableTemplates = getTemplatesForClause(questionClause);
                                              
                                              return (
                                                <Box>
                                                  {/* Implementation Text */}
                                                  {implementationText ? (
                                                    <Box
                                                      sx={{
                                                        p: 2,
                                                        backgroundColor: "rgba(33, 150, 243, 0.05)",
                                                        borderRadius: 2,
                                                        borderLeft: "4px solid #1976d2",
                                                        maxHeight: "200px",
                                                        overflowY: "auto",
                                                        mb: availableTemplates.length > 0 ? 2 : 0,
                                                        color: "#1976d2",
                                                        '& *': {
                                                          color: "#1976d2 !important"
                                                        }
                                                      }}
                                                      dangerouslySetInnerHTML={{ 
                                                        __html: implementationText 
                                                      }}
                                                    >
                                                    </Box>
                                                  ) : (
                                                   ''
                                                  )}

                                                  {/* Templates Section */}
                                                  {availableTemplates.length > 0 && (
                                                    <Box>
                                                      <Chip
                                                        icon={<Lightbulb />}
                                                        label={`Document for Compliance (${availableTemplates.length})`}
                                                        color="error"
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ mb: 1 }}
                                                      />
                                                      
                                                      <Box>
                                                        {availableTemplates.map((template, index) => (
                                                          <Box 
                                                            key={index}
                                                            sx={{ 
                                                              mt: 1,
                                                              p: 1.5,
                                                              backgroundColor: "rgba(255, 72, 0, 0.14)",
                                                              borderRadius: 1,
                                                              border: "1px solid rgba(255, 72, 0, 0.2)"
                                                            }}
                                                          >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                              <Box sx={{ flex: 1 }}>
                                                                <Box
                                                                  className="listform-html-content"
                                                                  sx={{
                                                                    color: "#5c1104ff",
                                                                    fontWeight: "medium",
                                                                    fontSize: "0.8rem",
                                                                    mb: 1
                                                                  }}
                                                                  dangerouslySetInnerHTML={{ 
                                                                    __html: template.Name || template.TemplateName || template.templateName || `Template ${index + 1}`
                                                                  }}
                                                                />
                                                                {template.Description && (
                                                                  <Box 
                                                                    className="listform-html-content"
                                                                    dangerouslySetInnerHTML={{ 
                                                                      __html: template.Description 
                                                                    }}
                                                                  />
                                                                )}
                                                              </Box>
                                                              
                                                              {/* Download button if PDF file is available */}
                                                              {template.Listfile && (
                                                                <Button
                                                                  variant="outlined"
                                                                  size="small"
                                                                  color="error"
                                                                  onClick={() => {
                                                                    window.open(template.Listfile, '_blank');
                                                                  }}
                                                                  sx={{
                                                                    ml: 1,
                                                                    minWidth: 'auto',
                                                                    px: 1,
                                                                    fontSize: '0.7rem',
                                                                    textTransform: 'none'
                                                                  }}
                                                                >
                                                                  Download PDF
                                                                </Button>
                                                              )}
                                                            </Box>
                                                            
                                                            {/* Show clause info for debugging */}
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                display: "block",
                                                                mt: 0.5,
                                                                color: "#999",
                                                                fontSize: "0.7rem"
                                                              }}
                                                            >
                                                              Clause: {template.clauseno}
                                                            </Typography>
                                                          </Box>
                                                        ))}
                                                      </Box>
                                                    </Box>
                                                  )}
                                                </Box>
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            {getNcOptionsForRequirement(
                                              question
                                            ).length > 0 ? (
                                              <>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    color: "#1976d2",
                                                    mb: 1,
                                                    display: "block",
                                                  }}
                                                >
                                                  Non-Compliance Items:
                                                </Typography>
                                                <FormGroup>
                                                  {getNcOptionsForRequirement(
                                                    question
                                                  ).map((ncOption, index) => {
                                                    const itemId = `${subSection.id}_q_${qIndex}`;
                                                    const questionClause = question.DisplayName || question.ClauseNo || question.clauseNo;
                                                    const evidenceKey = `${ncOption}_${questionClause}`;
                                                    const isChecked = (ncSelections.get(itemId) || new Set()).has(ncOption);
                                                    const hasEvidenceData = hasEvidence(ncOption, questionClause);
                                                    
                                                    return (
                                                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              checked={isChecked}
                                                              onChange={(e) =>
                                                                handleNcChange(
                                                                  itemId,
                                                                  ncOption,
                                                                  e.target.checked
                                                                )
                                                              }
                                                              size="small"
                                                              sx={{
                                                                color: "#f44336",
                                                                "&.Mui-checked": {
                                                                  color: "#f44336",
                                                                },
                                                              }}
                                                            />
                                                          }
                                                          label={
                                                            <Typography variant="caption">
                                                              {ncOption}
                                                            </Typography>
                                                          }
                                                          sx={{ flex: 1 }}
                                                        />
                                                        {isChecked && (
                                                          <Button
                                                            variant="contained"
                                                            size="small"
                                                            color={hasEvidenceData ? "success" : "error"}
                                                            onClick={() => handleEvidenceModalOpen(ncOption, questionClause)}
                                                            startIcon={hasEvidenceData ? <CheckCircle /> : <CloudUpload />}
                                                            sx={{
                                                              ml: 1,
                                                              minWidth: 'auto',
                                                              px: 1.5,
                                                              py: 0.5,
                                                              fontSize: '0.7rem',
                                                              textTransform: 'none',
                                                              fontWeight: 'bold',
                                                              borderRadius: 2,
                                                              boxShadow: 2,
                                                              '&:hover': {
                                                                boxShadow: 4,
                                                                transform: 'translateY(-1px)'
                                                              },
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            {hasEvidenceData ? 'View Details' : 'Upload Evidence'}
                                                          </Button>
                                                        )}
                                                      </Box>
                                                    );
                                                  })}
                                                </FormGroup>
                                              </>
                                            ) : (
                                              // <Typography
                                              //   variant="caption"
                                              //   sx={{
                                              //     color: "#666",
                                              //     fontStyle: "italic",
                                              //   }}
                                              // >
                                              //   No non-compliance options available
                                              // </Typography>
                                              ''
                                            )}
                                          </TableCell>
                                         
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            {(() => {
                                              const itemId = `${subSection.id}_q_${qIndex}`;
                                              const question = visibleQuestions[qIndex];
                                              const clauseNo = question.DisplayName || question.ClauseNo || question.clauseNo;
                                              const existingRemark = getExistingRemarkForClause(clauseNo);
                                              const hasExisting = hasExistingRemark(clauseNo);
                                              const ncOptions = getNcOptionsForRequirement(question);
                                              const hasNoNcOptions = ncOptions.length === 0;
                                              
                                              // Don't show remarks field if no NC options are available
                                              if (hasNoNcOptions) {
                                                return null;
                                              }
                                              
                                              if (hasExisting && existingRemark) {
                                                return (
                                                  <Alert 
                                                    severity="info" 
                                                    sx={{ 
                                                      backgroundColor: "#e3f2fd",
                                                      border: "1px solid #2196f3"
                                                    }}
                                                  >
                                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                      Existing Remark:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                      {existingRemark.Remark}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                      Saved on: {new Date(existingRemark.InsertDate).toLocaleDateString()}
                                                    </Typography>
                                                  </Alert>
                                                );
                                              } else {
                                                return (
                                                  <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    variant="outlined"
                                                    placeholder="Enter remarks..."
                                                    value={
                                                      remarks.get(itemId) || ""
                                                    }
                                                    onChange={(e) =>
                                                      handleRemarkChange(
                                                        itemId,
                                                        e.target.value
                                                      )
                                                    }
                                                    size="small"
                                                    sx={{
                                                      "& .MuiOutlinedInput-root": {
                                                        "&:hover fieldset": {
                                                          borderColor: "#1976d2",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                          borderColor: "#1976d2",
                                                        },
                                                      },
                                                      marginBottom: 1
                                                    }}
                                                  />
                                                );
                                              }
                                            })()}
                                            
                                            {(() => {
                                              const itemId = `${subSection.id}_q_${qIndex}`;
                                              const question = visibleQuestions[qIndex];
                                              const clauseNo = question.DisplayName || question.ClauseNo || question.clauseNo;
                                              const hasExisting = hasExistingRemark(clauseNo);
                                              const ncOptions = getNcOptionsForRequirement(question);
                                              const hasNoNcOptions = ncOptions.length === 0;
                                              
                                              // Don't show submit button if no NC options are available
                                              if (hasNoNcOptions) {
                                                return null;
                                              }
                                              
                                              // Don't show submit button if existing remarks are present
                                              if (hasExisting) {
                                                return null;
                                              }
                                              
                                              return (
                                                <Tooltip
                                                  title={getSubmitTooltip(itemId)}
                                                >
                                                  <span>
                                                    <Button
                                                      variant={
                                                        isSubmitted(itemId)
                                                          ? "outlined"
                                                          : "contained"
                                                      }
                                                      color={(() => {
                                                        const isItemSubmitted = isSubmitted(itemId);
                                                        const ncSelectionSet = ncSelections.get(itemId) || new Set();
                                                        const hasNcSelected = ncSelectionSet.size > 0;
                                                        
                                                        console.log(`Button color debug for ${itemId}:`, {
                                                          isItemSubmitted,
                                                          hasNcSelected,
                                                          ncSelectionsSize: ncSelectionSet.size,
                                                          selectedNCs: Array.from(ncSelectionSet)
                                                        });
                                                        
                                                        if (isItemSubmitted) {
                                                          return "success";
                                                        } else if (hasNcSelected) {
                                                          return "error";
                                                        } else {
                                                          return "success";
                                                        }
                                                      })()}
                                                      size="small"
                                                      onClick={() =>
                                                        handleSubmit(itemId)
                                                      }
                                                      disabled={isSubmitted(itemId)}
                                                      startIcon={
                                                        isSubmitted(itemId) ? (
                                                          <CheckCircle />
                                                        ) : (
                                                          <Send />
                                                        )
                                                      }
                                                      sx={{
                                                        minWidth: "120px",
                                                        fontWeight: "bold",
                                                        textTransform: "none",
                                                        borderRadius: 2,
                                                        transition: "all 0.3s ease",
                                                        ...(isSubmitted(itemId) && {
                                                          backgroundColor: "#e8f5e8",
                                                          borderColor: "#4caf50",
                                                          color: "#2e7d32",
                                                        }),
                                                      }}
                                                    >
                                                      {isSubmitted(itemId)
                                                        ? "Done"
                                                        : getSubmitButtonText(itemId)}
                                                    </Button>
                                                  </span>
                                                </Tooltip>
                                              );
                                            })()}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    }
                                    
                                    {/* If no questions, show the original requirements structure */}
                                    {(!subSection.questions || 
                                      subSection.questions.filter((question) => {
                                        const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                        const subsectionClause = subSection.clause;
                                        if (!questionClause || !subsectionClause) return true;
                                        return questionClause.toString() === subsectionClause.toString();
                                      }).length === 0) &&
                                      subSection.requirements.map(
                                      (requirement, reqIndex) => (
                                        <TableRow
                                          key={requirement.id}
                                          sx={{
                                            "&:nth-of-type(odd)": {
                                              backgroundColor: "#f8f9fa",
                                            },
                                            "&:nth-of-type(even)": {
                                              backgroundColor: "#ffffff",
                                            },
                                            "&:hover": {
                                              backgroundColor:
                                                "rgba(33, 150, 243, 0.08)",
                                            },
                                            "&:last-child .MuiTableCell-root": {
                                              borderBottom: "none",
                                            },
                                            transition: "all 0.2s ease",
                                          }}
                                        >
                                          <TableCell
                                            sx={{
                                              minWidth: 100,
                                              fontWeight: "bold",
                                              color: "#1976d2",
                                              fontSize: "1.1rem",
                                              p: 2,
                                            }}
                                          >
                                            {requirement.clause}
                                            {requirement.subclause}
                                          </TableCell>
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ mb: 2, lineHeight: 1.6 }}
                                            >
                                              {requirement.requirement}
                                            </Typography>

                                            {/* Display DAQMS Questions in a clean section */}
                                            {subSection.questions &&
                                              subSection.questions.length >
                                                0 && 
                                              subSection.questions.filter((question) => {
                                                // Only show questions that match the current subsection clause
                                                const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                                const subsectionClause = subSection.clause;
                                                
                                                // If no clause info, show all questions for this subsection
                                                if (!questionClause || !subsectionClause) {
                                                  return true;
                                                }
                                                
                                                // Match clause numbers
                                                return questionClause.toString() === subsectionClause.toString();
                                              }).length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                  <Typography
                                                    variant="caption"
                                                    color="#1976d2"
                                                    sx={{
                                                      fontWeight: "bold",
                                                      mb: 1,
                                                      display: "block",
                                                    }}
                                                  >
                                                    DAQMS Questions (
                                                    {
                                                      subSection.questions.filter((question) => {
                                                        const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                                        const subsectionClause = subSection.clause;
                                                        if (!questionClause || !subsectionClause) return true;
                                                        return questionClause.toString() === subsectionClause.toString();
                                                      }).length
                                                    }
                                                    ):
                                                  </Typography>
                                                  {subSection.questions
                                                    .filter((question) => {
                                                      // Filter questions to match current subsection
                                                      const questionClause = question.ClauseNo || question.clauseNo || question.clause;
                                                      const subsectionClause = subSection.clause;
                                                      if (!questionClause || !subsectionClause) return true;
                                                      return questionClause.toString() === subsectionClause.toString();
                                                    })
                                                    .map(
                                                    (question, qIndex) => (
                                                      <Box
                                                        key={qIndex}
                                                        sx={{
                                                          mb: 1,
                                                          p: 1.5,
                                                          backgroundColor:
                                                            "#f0f8ff",
                                                          borderRadius: 1,
                                                          border:
                                                            "1px solid #e3f2fd",
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body2"
                                                          sx={{
                                                            fontSize: "0.9rem",
                                                            mb: 0.5,
                                                          }}
                                                        >
                                                          <strong>
                                                            Q{qIndex + 1}:
                                                          </strong>{" "}
                                                          {
                                                            question.QuestionText
                                                          }
                                                        </Typography>
                                                        {question.Options && (
                                                          <Typography
                                                            variant="caption"
                                                            sx={{
                                                              color:
                                                                "text.secondary",
                                                              fontStyle:
                                                                "italic",
                                                            }}
                                                          >
                                                            Options:{" "}
                                                            {question.Options}
                                                          </Typography>
                                                        )}
                                                      </Box>
                                                    )
                                                  )}
                                                </Box>
                                              )}
                                          </TableCell>
                                          <TableCell sx={{ p: 2 }}>
                                            {requirement.implementation && (
                                              <Box
                                                sx={{
                                                  p: 2,
                                                  backgroundColor:
                                                    "rgba(33, 150, 243, 0.05)",
                                                  borderRadius: 2,
                                                  borderLeft:
                                                    "4px solid #1976d2",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="#1976d2"
                                                  sx={{
                                                    lineHeight: 1.5,
                                                    fontSize: "0.8rem",
                                                  }}
                                                >
                                                  {requirement.implementation}
                                                </Typography>
                                              </Box>
                                            )}
                                            {(() => {
                                              // Get dynamic templates from database
                                              const requirementClause = requirement.clause;
                                              const availableTemplates = getTemplatesForClause(requirementClause);
                                              
                                              // Show database templates if available, otherwise show static templates
                                              const hasTemplates = availableTemplates.length > 0 || requirement.templates;
                                              
                                              if (hasTemplates) {
                                                return (
                                                  <Box sx={{ mt: 2 }}>
                                                    <Chip
                                                      icon={<Lightbulb />}
                                                      label={`Templates Available (${availableTemplates.length || 1})`}
                                                      color="success"
                                                      variant="outlined"
                                                      size="small"
                                                    />
                                                    
                                                    {/* Dynamic templates from ListFormats table */}
                                                    {availableTemplates.length > 0 && (
                                                      <Box sx={{ mt: 1 }}>
                                                        {availableTemplates.map((template, index) => (
                                                          <Box 
                                                            key={index}
                                                            sx={{ 
                                                              mt: 1,
                                                              p: 1.5,
                                                              backgroundColor: "rgba(255, 152, 0, 0.05)",
                                                              borderRadius: 1,
                                                              border: "1px solid rgba(255, 152, 0, 0.2)"
                                                            }}
                                                          >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                              <Box sx={{ flex: 1 }}>
                                                                <Typography
                                                                  variant="caption"
                                                                  sx={{
                                                                    display: "block",
                                                                    color: "#39f500ff",
                                                                    fontWeight: "medium",
                                                                    fontSize: "0.8rem"
                                                                  }}
                                                                >
                                                                  {template.Name || template.TemplateName || template.templateName || `Template ${index + 1}`}
                                                                </Typography>
                                                                {template.Description && (
                                                                  <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                      display: "block",
                                                                      mt: 0.5,
                                                                      color: "#666",
                                                                      fontStyle: "italic",
                                                                      fontSize: "0.75rem"
                                                                    }}
                                                                  >
                                                                    {template.Description}
                                                                  </Typography>
                                                                )}
                                                              </Box>
                                                              
                                                              {/* Download button if PDF file is available */}
                                                              {template.Listfile && (
                                                                <Button
                                                                  variant="outlined"
                                                                  size="small"
                                                                  color="warning"
                                                                  onClick={() => {
                                                                    window.open(template.Listfile, '_blank');
                                                                  }}
                                                                  sx={{
                                                                    ml: 1,
                                                                    minWidth: 'auto',
                                                                    px: 1,
                                                                    fontSize: '0.7rem',
                                                                    textTransform: 'none'
                                                                  }}
                                                                >
                                                                  Download PDF
                                                                </Button>
                                                              )}
                                                            </Box>
                                                            
                                                            {/* Show clause info for debugging */}
                                                            <Typography
                                                              variant="caption"
                                                              sx={{
                                                                display: "block",
                                                                mt: 0.5,
                                                                color: "#999",
                                                                fontSize: "0.7rem"
                                                              }}
                                                            >
                                                              Clause: {template.clauseno} | ID: {template.id}
                                                            </Typography>
                                                          </Box>
                                                        ))}
                                                      </Box>
                                                    )}
                                                    
                                                    {/* Fallback to static templates if no dynamic templates */}
                                                    {availableTemplates.length === 0 && requirement.templates && (
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          display: "block",
                                                          mt: 1,
                                                          color: "#666",
                                                          fontStyle: "italic",
                                                        }}
                                                      >
                                                        {requirement.templates}
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                );
                                              }
                                              return null;
                                            })()}
                                          </TableCell>
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            {getNcOptionsForRequirement(
                                              requirement
                                            ).length > 0 ? (
                                              <>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: "bold",
                                                    color: "#1976d2",
                                                    mb: 1,
                                                    display: "block",
                                                  }}
                                                >
                                                  Non-Compliance Items:
                                                </Typography>
                                                <FormGroup>
                                                  {getNcOptionsForRequirement(
                                                    requirement
                                                  ).map((ncOption, index) => {
                                                    const isChecked = (ncSelections.get(requirement.id) || new Set()).has(ncOption);
                                                    const clauseNo = requirement.clause;
                                                    const hasEvidenceData = hasEvidence(ncOption, clauseNo);
                                                    
                                                    return (
                                                      <Box key={index} sx={{  mb: 1 }}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              checked={isChecked}
                                                              onChange={(e) =>
                                                                handleNcChange(
                                                                  requirement.id,
                                                                  ncOption,
                                                                  e.target.checked
                                                                )
                                                              }
                                                              size="small"
                                                              sx={{
                                                                color: "#f44336",
                                                                "&.Mui-checked": {
                                                                  color: "#f44336",
                                                                },
                                                              }}
                                                            />
                                                          }
                                                          label={
                                                            <Typography variant="caption">
                                                              {ncOption}
                                                            </Typography>
                                                          }
                                                          sx={{ flex: 1 }}
                                                        /> 
                                                        {isChecked && (
                                                          <div style={{ display: 'flex', alignItems: 'center' }}>
                                                          <Button
                                                            variant="contained"
                                                            size="small"
                                                            color={hasEvidenceData ? "success" : "error"}
                                                            onClick={() => handleEvidenceModalOpen(ncOption, clauseNo)}
                                                            startIcon={hasEvidenceData ? <CheckCircle /> : <CloudUpload />}
                                                            sx={{
                                                              ml: 1,
                                                              minWidth: 'auto',
                                                              px: 1.5,
                                                              py: 0.5,
                                                              fontSize: '0.7rem',
                                                              textTransform: 'none',
                                                              fontWeight: 'bold',
                                                              borderRadius: 2,
                                                              boxShadow: 2,
                                                              '&:hover': {
                                                                boxShadow: 4,
                                                                transform: 'translateY(-1px)'
                                                              },
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            {hasEvidenceData ? 'View Details' : 'Upload Evidence'}
                                                          </Button>
                                                          </div>
                                                        )}
                                                      </Box>
                                                    );
                                                  })}
                                                </FormGroup>
                                              </>
                                            ) : null}
                                          </TableCell>
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            {(() => {
                                              const clauseNo = requirement.clause;
                                              const existingRemark = getExistingRemarkForClause(clauseNo);
                                              const hasExisting = hasExistingRemark(clauseNo);
                                              const ncOptions = getNcOptionsForRequirement(requirement);
                                              const hasNoNcOptions = ncOptions.length === 0;
                                              
                                              // Don't show remarks field if no NC options are available
                                              if (hasNoNcOptions) {
                                                return null;
                                              }
                                              
                                              if (hasExisting && existingRemark) {
                                                return (
                                                  <Alert 
                                                    severity="info" 
                                                    sx={{ 
                                                      backgroundColor: "#e3f2fd",
                                                      border: "1px solid #2196f3"
                                                    }}
                                                  >
                                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                      Existing Remark:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                      {existingRemark.Remark}
                                                    </Typography>
                                                  </Alert>
                                                );
                                              } else {
                                                return (
                                                  <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    variant="outlined"
                                                    placeholder="Enter remarks..."
                                                    value={
                                                      remarks.get(requirement.id) ||
                                                      ""
                                                    }
                                                    onChange={(e) =>
                                                      handleRemarkChange(
                                                        requirement.id,
                                                        e.target.value
                                                      )
                                                    }
                                                    size="small"
                                                    sx={{
                                                      "& .MuiOutlinedInput-root": {
                                                        "&:hover fieldset": {
                                                          borderColor: "#1976d2",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                          borderColor: "#1976d2",
                                                        },
                                                      },
                                                    }}
                                                  />
                                                );
                                              }
                                            })()}
                                          </TableCell>
                                          <TableCell
                                            sx={{ minWidth: 200, p: 2 }}
                                          >
                                            {(() => {
                                              const clauseNo = requirement.clause;
                                              const hasExisting = hasExistingRemark(clauseNo);
                                              const ncOptions = getNcOptionsForRequirement(requirement);
                                              const hasNoNcOptions = ncOptions.length === 0;
                                              
                                              // Don't show submit button if no NC options are available
                                              if (hasNoNcOptions) {
                                                return null;
                                              }
                                              
                                              // Don't show submit button if existing remarks are present
                                              if (hasExisting) {
                                                return null;
                                              }
                                              
                                              return (
                                                <Tooltip
                                                  title={getSubmitTooltip(requirement.id)}
                                                >
                                                  <span>
                                                    <Button
                                                      variant={
                                                        isSubmitted(requirement.id)
                                                          ? "outlined"
                                                          : "contained"
                                                      }
                                                      color={
                                                        isSubmitted(requirement.id)
                                                          ? "success"
                                                          : (ncSelections.get(requirement.id) || new Set()).size > 0
                                                          ? "error"
                                                          : "primary"
                                                      }
                                                      size="small"
                                                      onClick={() =>
                                                        handleSubmit(requirement.id)
                                                      }
                                                      disabled={isSubmitted(requirement.id)}
                                                      startIcon={
                                                        isSubmitted(requirement.id) ? (
                                                          <CheckCircle />
                                                        ) : (
                                                          <Send />
                                                        )
                                                      }
                                                      sx={{
                                                        minWidth: "120px",
                                                        fontWeight: "bold",
                                                        textTransform: "none",
                                                        borderRadius: 2,
                                                        transition: "all 0.3s ease",
                                                        ...(isSubmitted(requirement.id) && {
                                                          backgroundColor: "#e8f5e8",
                                                          borderColor: "#4caf50",
                                                          color: "#2e7d32",
                                                        }),
                                                      }}
                                                    >
                                                      {isSubmitted(requirement.id)
                                                        ? "Done"
                                                        : getSubmitButtonText(requirement.id)}
                                                    </Button>
                                                  </span>
                                                </Tooltip>
                                              );
                                            })()}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                      }
                                      
                                      {/* Load More Questions Button */}
                                      {hasMoreQuestions && (
                                        <TableRow>
                                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => {
                                                  console.log(`Load More clicked for ${sectionId}: current=${currentLimit}, total=${totalQuestions}`);
                                                  loadMoreQuestions(sectionId, totalQuestions);
                                                }}
                                                startIcon={<ExpandMore />}
                                              >
                                                Load 5 More Questions
                                              </Button>
                                              
                                              {totalQuestions - currentLimit > 5 && (
                                                <Button
                                                  variant="text"
                                                  size="small"
                                                  onClick={() => {
                                                    console.log(`Load All clicked for ${sectionId}: current=${currentLimit}, total=${totalQuestions}`);
                                                    loadAllQuestions(sectionId, totalQuestions);
                                                  }}
                                                  sx={{ ml: 1 }}
                                                >
                                                  Load All ({totalQuestions - currentLimit} remaining)
                                                </Button>
                                              )}
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                      
                                      {/* Performance Info */}
                                      {totalQuestions > 3 && (
                                        <TableRow>
                                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 1, backgroundColor: '#f8f9fa' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Showing {Math.min(currentLimit, totalQuestions)} of {totalQuestions} questions for better performance
                                              {currentLimit >= totalQuestions && totalQuestions > 3 && (
                                                <span style={{ color: '#4caf50', fontWeight: 'bold' }}> • All questions loaded</span>
                                              )}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </>
                                    );
                                    })()}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </Card>
            ))}

      {/* Evidence Modal */}
      <Dialog
        open={evidenceModal.open}
        onClose={handleEvidenceModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" color="error" fontWeight="bold">
                📋 Non-Compliance Evidence
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {evidenceModal.ncItem}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Clause: {evidenceModal.clauseNo}
              </Typography>
            </Box>
            <IconButton onClick={handleEvidenceModalClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* File Upload Section */}
          <Card variant="outlined" sx={{ mb: 3, p: 3, backgroundColor: '#fafafa' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
              Upload Evidence File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload an image file that provides evidence for this non-compliance item.
            </Typography>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <Button
              variant="outlined"
              component="span"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<CloudUpload />}
              size="large"
              sx={{ 
                mb: 2,
                width: '100%',
                py: 1.5,
                borderStyle: 'dashed',
                borderWidth: 2,
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              }}
            >
              {evidenceModal.file ? 'Change Image' : 'Select Image File'}
            </Button>
            
            {evidenceModal.file && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  📄 Selected: {evidenceModal.file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Size: {(evidenceModal.file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Alert>
            )}
            
            {evidenceModal.imagePreview && (
              <Box sx={{ mt: 2, textAlign: 'center', p: 2, border: '2px dashed #ddd', borderRadius: 2 }}>
                <Typography variant="body2" gutterBottom color="text.secondary">
                  📷 Image Preview:
                </Typography>
                <img
                  src={evidenceModal.imagePreview}
                  alt="Evidence Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            )}
          </Card>
          
          {/* Remarks Section */}
          <Card variant="outlined" sx={{ mb: 3, p: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              📝 Additional Remarks
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide detailed remarks, observations, or additional context for this non-compliance.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Enter detailed remarks about this non-compliance item..."
              value={evidenceModal.notes}
              onChange={(e) => setEvidenceModal(prev => ({
                ...prev,
                notes: e.target.value
              }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 Tip: Include specific details about the non-compliance, its impact, and any corrective actions needed.
            </Typography>
          </Card>

          {/* Status Messages */}
          {evidenceModal.uploadStatus && (
            <Alert 
              severity={evidenceModal.uploadStatus.includes('Error') ? 'error' : 'success'}
              sx={{ mb: 2 }}
            >
              {evidenceModal.uploadStatus}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleEvidenceModalClose}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            // onClick={handleSaveEvidence}
            variant="contained"
            disabled={!evidenceModal.file && !evidenceModal.notes.trim()}
            startIcon={uploading ? <CircularProgress size={16} /> : <Save />}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 'bold',
              borderRadius: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {uploading ? 'Uploading...' : 'Save Evidence & Remarks'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default SubmitDA;
